<template><div><h1 id="macos-hibernation" tabindex="-1"><a class="header-anchor" href="#macos-hibernation"><span>macOS Hibernation</span></a></h1>
<p>macOS by default will try to use hibernation after sleeping for a predetermined amount of time.<br>
Some fixes in the <code v-pre>config.plist</code> are required in order to take advantage of this:</p>
<ul>
<li>
<p>Disable <code v-pre>Booter-&gt;Quirks-&gt;DevirtualiseMmio</code></p>
</li>
<li>
<p>Set <code v-pre>Misc-&gt;Boot-&gt;HibernateMode</code> to <code v-pre>Auto</code></p>
<ul>
<li>By default, OpenCore will not look for hibernated images of macOS.</li>
</ul>
</li>
<li>
<p>Set <code v-pre>Misc-&gt;Boot-&gt;HibernationSkipsPicker</code> to <code v-pre>True</code> to skip the picker when resuming from hibernation</p>
<ul>
<li>While not strictly required, this speeds up resume times and prevents accidently booting another OS and changing hardware state underneath macOS.</li>
</ul>
</li>
<li>
<p>Create a reserved memory region under <code v-pre>UEFI-&gt;ReservedMemory</code> with the properties:</p>
<table>
<thead>
<tr>
<th>Field</th>
<th>Type</th>
<th>Value</th>
</tr>
</thead>
<tbody>
<tr>
<td>Comment</td>
<td>String</td>
<td>Fix black screen on wake from hibernation</td>
</tr>
<tr>
<td>Enabled</td>
<td>Boolean</td>
<td>True</td>
</tr>
<tr>
<td>Address</td>
<td>Number</td>
<td>569344</td>
</tr>
<tr>
<td>Enabled</td>
<td>Boolean</td>
<td>True</td>
</tr>
<tr>
<td>Size</td>
<td>Number</td>
<td>4096</td>
</tr>
<tr>
<td>Type</td>
<td>String</td>
<td>RuntimeCode</td>
</tr>
</tbody>
</table>
</li>
</ul>
<h2 id="testing-hibernation" tabindex="-1"><a class="header-anchor" href="#testing-hibernation"><span>Testing Hibernation</span></a></h2>
<p>To test hibernation, it is recommended to run <code v-pre>sudo pmset -a hibernatemode 25</code>.<br>
This will force macOS to hibernate immediately whenever the lid is closed or <code v-pre>Sleep</code> is selected in the menu at the top left.</p>
<h3 id="macos-enters-s3-sleep-instead-of-shutting-down" tabindex="-1"><a class="header-anchor" href="#macos-enters-s3-sleep-instead-of-shutting-down"><span>macOS enters S3 sleep instead of shutting down</span></a></h3>
<p>Some models (such as <code v-pre>KLED</code>) have drives not marked as internal, which prevents macOS from entering hibernation.<br>
To fix this, add the <code v-pre>built-in</code> property to the PCI device under <code v-pre>DeviceProperties-&gt;Add-&gt;&lt;storage device path&gt;</code>:</p>
<table>
<thead>
<tr>
<th>Field</th>
<th>Type</th>
<th>Value</th>
</tr>
</thead>
<tbody>
<tr>
<td>built-in</td>
<td>Data</td>
<td>&lt;01000000&gt;</td>
</tr>
</tbody>
</table>
<p>You may need to add an ACPI device as well.
If an ACPI device does not exist to represent the NVMe or eMMC drive, then device properties will not be applied.
An example SSDT can be found <a href="https://github.com/1Revenger1/Acer-Spin-713-Hackintosh/blob/main/src/ACPI/SSDT-Devices.dsl" target="_blank" rel="noopener noreferrer">here</a>.</p>
<h2 id="hibernate-modes" tabindex="-1"><a class="header-anchor" href="#hibernate-modes"><span>Hibernate Modes</span></a></h2>
<p>Pmset exposes a few different hibernation modes which can be set through <code v-pre>sudo pmset -a hibernatemode &lt;value&gt;</code>:</p>
<ul>
<li>0: Disables hibernation</li>
<li>3: Hibernates after either <code v-pre>standbydelaylow</code> or <code v-pre>standbydelayhigh</code> seconds of sleep
<ul>
<li>Which one is used is dependent on the battery percentage. Anything above <code v-pre>highstandbythreshold</code> will use <code v-pre>standbydelayhigh</code> instead of <code v-pre>standbydelaylow</code>.</li>
</ul>
</li>
<li>25: Hibernates instead of S3 sleep</li>
</ul>
<p>When using hibernate mode 3, macOS may keep sleeping even after the standby time has elapsed.
<a href="https://github.com/acidanthera/HibernationFixup" target="_blank" rel="noopener noreferrer"><code v-pre>HibernationFixup.kext</code></a> may be required in this case, and provides options to fine tune when the Chromebook is allowed to sleep.</p>
</div></template>


